const RAW_DBQ_CONDITIONS = `
Cardiovascular
* Heart condition
* Hypertension
* Artery or vein condition

Dermatological
* Skin condition
* Scars

Endocrinological
* Diabetes mellitus
* Thyroid condition
* Endocrine condition

Ear, Nose, Throat
* Ear condition
* Loss of sense of smell or taste
* Sinus, nose, throat, larynx, or pharynx condition

Gastrointestinal
* Esophageal condition
* Gallbladder condition
* Intestinal condition
* Liver condition
* Pancreas condition
* Rectum or anus condition
* Stomach or duodenum condition

Genitourinary
* Kidney condition
* Male reproductive organ condition
* Urinary tract condition

Gynecological
* Breast condition
* Gynecological condition

Musculoskeletal
Spine
  * Neck (cervical spine) condition
    * Left upper extremity radiculopathy
    * Right upper extremity radiculopathy
  * Back (thoracolumbar spine) condition
    * Left lower extremity radiculopathy
    * Right lower extremity radiculopathy
Upper Extremities
* Shoulder condition (left or right)
* Elbow condition (left or right)
* Wrist condition (left or right)
* Hand or finger condition (left or right)
Lower Extremities
* Hip condition (left or right)
* Knee condition (left or right)
* Ankle condition (left or right)
* Foot condition (left or right)
Other Musculoskeletal
* Amputation condition (left or right)
* Temporomandibular joint (TMJ) condition

Neurological
* Peripheral nerve condition (left or right)
* Diabetic peripheral neuropathy (left or right)
* Headaches
* Parkinson's disease

Ophthalmological
* Eye condition (left, right, or bilateral)

Psychological
* Mental health condition (other than PTSD)
* PTSD

Respiratory
* Respiratory condition
* Sleep apnea

Rheumatological / Systemic
* Chronic fatigue syndrome
`;

export const DBQ_CONTENTION_DELIMITER = '\n';

const expandSelectableLeafLabels = (leafLabel) => {
  if (!leafLabel.includes('(left or right)')) {
    return [leafLabel];
  }

  return [
    leafLabel.replace('(left or right)', '(left)').replace(/\s+/g, ' ').trim(),
    leafLabel.replace('(left or right)', '(right)').replace(/\s+/g, ' ').trim(),
  ];
};

const buildDbqContentionOption = ({ rootLabel, pathLabels = [], leafLabel }) => {
  const value = [rootLabel, ...pathLabels, leafLabel].join(' > ');
  return {
    value,
    label: leafLabel,
    leafLabel,
    rootLabel,
    pathLabel: pathLabels.join(' / '),
  };
};

const parseDbqConditionBlocks = (rawText) => {
  return String(rawText || '')
    .trim()
    .split(/\n\s*\n/)
    .map((block) => block.split('\n').map((line) => line.replace(/\s+$/, '')))
    .filter((block) => block.length > 0);
};

const buildDbqContentionOptionGroups = (rawText) => {
  return parseDbqConditionBlocks(rawText).map((lines) => {
    const [rootLine, ...bodyLines] = lines;
    const rootLabel = rootLine.trim();
    const rootNode = { label: rootLabel, level: 0 };
    const stack = [rootNode];
    let currentFlatParent = rootNode;

    const options = bodyLines.reduce((accumulator, rawLine) => {
      const indent = rawLine.match(/^\s*/)?.[0]?.length || 0;
      const trimmed = rawLine.trim();
      if (!trimmed) return accumulator;

      const isSelectable = trimmed.startsWith('*');
      const label = trimmed.replace(/^\*\s*/, '').trim();

      if (!isSelectable) {
        const level = Math.floor(indent / 2) + 1;
        stack.length = Math.max(1, level);
        const node = { label, level };
        stack[level] = node;
        currentFlatParent = node;
        return accumulator;
      }

      const level = indent === 0 ? currentFlatParent.level + 1 : Math.floor(indent / 2) + 1;
      stack.length = Math.max(1, level);
      const pathLabels = stack.slice(1).map((node) => node.label);

      expandSelectableLeafLabels(label).forEach((expandedLeafLabel) => {
        accumulator.push(
          buildDbqContentionOption({
            rootLabel,
            pathLabels,
            leafLabel: expandedLeafLabel,
          }),
        );
      });

      stack[level] = { label, level };
      return accumulator;
    }, []);

    return {
      label: rootLabel,
      options,
    };
  });
};

export const DBQ_CONTENTION_OPTION_GROUPS = buildDbqContentionOptionGroups(RAW_DBQ_CONDITIONS);

const DBQ_CONTENTION_OPTION_MAP = new Map(
  DBQ_CONTENTION_OPTION_GROUPS.flatMap((group) =>
    group.options.map((option) => [option.value, option]),
  ),
);

export const parseStoredDbqContentions = (value) =>
  String(value || '')
    .split(DBQ_CONTENTION_DELIMITER)
    .map((entry) => entry.trim())
    .filter(Boolean);

export const serializeDbqContentions = (values = []) =>
  [...new Set((Array.isArray(values) ? values : []).map((value) => String(value || '').trim()).filter(Boolean))]
    .join(DBQ_CONTENTION_DELIMITER);

export const findDbqContentionOptions = (values = []) =>
  parseStoredDbqContentions(Array.isArray(values) ? values.join(DBQ_CONTENTION_DELIMITER) : values).map(
    (value) =>
      DBQ_CONTENTION_OPTION_MAP.get(value) || {
        value,
        label: value.split(' > ').slice(-1)[0] || value,
        leafLabel: value.split(' > ').slice(-1)[0] || value,
        rootLabel: 'Saved Selection',
        pathLabel: value.split(' > ').slice(1, -1).join(' / '),
      },
  );

export const getDbqContentionDisplayLabel = (value) => {
  const option = DBQ_CONTENTION_OPTION_MAP.get(value);
  if (option) return option.leafLabel;
  return value.split(' > ').slice(-1)[0] || value;
};
